import React from 'react';
import Yup from '~/util/yup';
import { SelectFormField } from '~/shared/forms/controls/fields/SelectFormField';
import { TextFormField } from '~/shared/forms/controls/fields/text-fields/TextFormField';
import type { ImmunizationModel } from '../types';
import { TextAreaFormField } from '~/shared/forms/controls/fields/text-fields/TextAreaFormField';
import { FormModal } from '~/shared/forms/FormModal';
import { DatePickerFormField } from '~/shared/forms/controls/fields/date-fields/DatePickerFormField';
import {
	addPatientImmunizationOptions,
	getImmunizationsOptions,
	ImmunizationAdministererOptions,
	ImmunizationTypeOptions,
	editPatientImmunizationOptions,
	ImmunizationTypeEnum,
	ImmunizationAdministererEnum
} from '@generated';
import { useQuery } from '@tanstack/react-query';

const validationSchema = Yup.object().shape({
	immunizationTypeId: Yup.string().nullable().required('Required'),
	immunizationAdministererId: Yup.string().nullable().required('Required'),
	dateAdministered: Yup.date().nullable().required('Required'),
	lotNumber: Yup.string().nullable(),
	immunizationTypeOther: Yup.string().nullable().otherReason('immunizationTypeId', ImmunizationTypeEnum.Other),
	immunizationAdministererOther: Yup.string().nullable().otherReason('immunizationAdministererId', ImmunizationAdministererEnum.Other)
});

export function PatientImmunizationFormModal({
	stayId,
	existingImmunization,
	quickAddImmunizationTypeId,
	closeModal
}: {
	stayId: string;
	existingImmunization?: ImmunizationModel;
	quickAddImmunizationTypeId?: number;
	closeModal: () => void;
}) {
	const { data: patientImmunizations, isLoading } = useQuery(getImmunizationsOptions(stayId));

	if (isLoading || !patientImmunizations) {
		return null;
	}

	const initialValues = existingImmunization
		? {
				...existingImmunization,
				immunizationId: existingImmunization.id
		  }
		: {
				stayId,
				immunizationTypeId: quickAddImmunizationTypeId || null,
				immunizationTypeOther: null,
				immunizationAdministererId: null,
				dateAdministered: null,
				lotNumber: null
		  };

	return (
		<FormModal
			title={`${existingImmunization ? 'Edit' : 'Add'} Immunization`}
			initialValues={initialValues}
			saveMutation={{
				...(existingImmunization ? editPatientImmunizationOptions : addPatientImmunizationOptions),
				invalidateQueryKeys: [getImmunizationsOptions(stayId).queryKey]
			}}
			validationSchema={validationSchema}
			onSuccess={closeModal}
			closeModal={closeModal}
		>
			{formHelpers => {
				const formValues = formHelpers?.values;
				return (
					<div className="grid grid-cols-1 gap-4">
						<div className="col-span-1 grid grid-cols-1 gap-6">
							<SelectFormField name="immunizationTypeId" label="Intervention Type" options={ImmunizationTypeOptions} />
							{formValues?.immunizationTypeId === '99' && (
								<TextFormField name="immunizationTypeOther" label="Other Intervention Type" />
							)}
							<SelectFormField name="immunizationAdministererId" label="Administered By" options={ImmunizationAdministererOptions} />
							{formValues?.immunizationAdministererId === '99' && (
								<TextFormField name="immunizationAdministererOther" label="Other Administerer" />
							)}

							<DatePickerFormField name="dateAdministered" label="Date Administered" />
							<TextFormField name="lotNumber" label="Lot Number" />
							<TextAreaFormField name="notes" label="Notes" />
						</div>
					</div>
				);
			}}
		</FormModal>
	);
}
